/**
 * VeraLok AI chat widget — public marketing site only.
 *
 * Design choices:
 *  - Default state: **collapsed** floating trigger, bottom-right.
 *  - Session-scoped conversation via POST /api/marketing/chat.
 *  - Backend assistant is prompt-restricted to high-level answers; it
 *    refuses to reveal proprietary architecture, hash choices, adapter
 *    names, endpoint paths, or anything similarly internal.
 *  - Panel becomes a full-width bottom-sheet on ≤640px viewports
 *    (styles applied via .vlk-chat-panel in design-system.css).
 *  - Fully keyboard accessible: Escape closes, focus moves to input,
 *    the panel is a proper labelled dialog.
 */
import React, { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, Send, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const GREETING =
  "Hi — I'm the VeraLok assistant. Ask about our mission, our security " +
  "principles, industries we serve, or how to book a demo.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false); // deliberately collapsed by default
  const [sessionId] = useState(() =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2)
  );
  const [msgs, setMsgs] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    // return focus to the trigger for keyboard users
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  // Focus the input when the panel opens.
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Autoscroll on new messages / open.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const r = await api.post("/marketing/chat", { session_id: sessionId, message: text });
      setMsgs((m) => [...m, { role: "assistant", content: r.data?.reply || "(no reply)" }]);
    } catch (err) {
      setMsgs((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "I'm briefly offline. For a person, use Contact or Request a demo — links are in the site header.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-50 vlk-btn-primary shadow-2xl"
        style={{ padding: "0.9rem 1.1rem" }}
        aria-label={open ? "Close VeraLok assistant" : "Open VeraLok assistant"}
        aria-expanded={open}
        aria-controls="vlk-chat-panel"
        data-testid="chat-trigger"
      >
        {open ? <X className="h-4 w-4" aria-hidden="true" /> : <MessageCircle className="h-4 w-4" aria-hidden="true" />}
        <span className="hidden sm:inline">{open ? "Close" : "Ask VeraLok"}</span>
      </button>

      {open && (
        <div
          id="vlk-chat-panel"
          className="vlk-chat-panel vlk-glass"
          role="dialog"
          aria-modal="false"
          aria-label="VeraLok assistant"
          data-testid="chat-panel"
        >
          <div className="p-4 border-b border-white/10 flex items-center gap-2">
            <div
              className="h-2 w-2 rounded-full bg-[color:var(--vlk-brand-400)]"
              style={{ boxShadow: "0 0 8px var(--vlk-brand-400)" }}
              aria-hidden="true"
            />
            <div className="text-sm font-semibold">VeraLok assistant</div>
            <div className="ml-auto text-[10px] uppercase tracking-widest text-white/50">Beta</div>
            <button
              type="button"
              onClick={close}
              className="p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition"
              aria-label="Close VeraLok assistant"
              data-testid="chat-close"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 text-sm"
            data-testid="chat-messages"
          >
            {msgs.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] p-3 rounded-2xl leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-[color:var(--vlk-brand-500)] text-white whitespace-pre-wrap"
                    : "mr-auto bg-white/8 text-white/90 border border-white/10"
                }`}
              >
                {m.role === "assistant" ? <AssistantMarkdown text={m.content} /> : m.content}
              </div>
            ))}
            {busy && (
              <div className="mr-auto text-white/60 text-xs flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> thinking…
              </div>
            )}
          </div>

          <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2" data-testid="chat-form">
            <label htmlFor="vlk-chat-input" className="sr-only">Ask about VeraLok</label>
            <input
              id="vlk-chat-input"
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about VeraLok…"
              disabled={busy}
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white outline-none focus:border-[color:var(--vlk-brand-400)]"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              className="vlk-btn-primary disabled:opacity-50"
              style={{ padding: "0.5rem 0.9rem" }}
              aria-label="Send message"
              data-testid="chat-send"
            >
              <Send className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

/**
 * Lightweight Markdown renderer for assistant replies. The upstream
 * (Claude) returns Markdown by convention — we render the small subset
 * the model actually uses so the widget never displays raw `**foo**`
 * or `- bullet` syntax. No dangerouslySetInnerHTML; every element is a
 * plain React node.
 *
 * Supported:
 *   • `**bold**` and `__bold__`
 *   • `*italic*` and `_italic_`
 *   • `` `code` ``
 *   • `## h2`, `### h3`
 *   • `- ` and `* ` bullet lists
 *   • `1. ` numbered lists
 *   • blank line → paragraph break
 */
function AssistantMarkdown({ text }) {
  const lines = String(text || "").split(/\r?\n/);
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*$/.test(line)) { i++; continue; }
    // Headings
    let m = line.match(/^(#{2,3})\s+(.+)$/);
    if (m) {
      const Tag = m[1].length === 2 ? "h3" : "h4";
      blocks.push(
        <Tag key={blocks.length} className="font-semibold text-white/90 mt-2 mb-1 text-sm">
          {renderInline(m[2])}
        </Tag>
      );
      i++;
      continue;
    }
    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i++;
      }
      blocks.push(
        <ul key={blocks.length} className="list-disc pl-5 space-y-0.5 my-1">
          {items.map((it, k) => <li key={k}>{renderInline(it)}</li>)}
        </ul>
      );
      continue;
    }
    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i++;
      }
      blocks.push(
        <ol key={blocks.length} className="list-decimal pl-5 space-y-0.5 my-1">
          {items.map((it, k) => <li key={k}>{renderInline(it)}</li>)}
        </ol>
      );
      continue;
    }
    // Paragraph — collapse a run of non-blank lines
    const para = [];
    while (i < lines.length && !/^\s*$/.test(lines[i]) &&
           !/^(#{2,3})\s+/.test(lines[i]) &&
           !/^\s*[-*]\s+/.test(lines[i]) &&
           !/^\s*\d+\.\s+/.test(lines[i])) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={blocks.length} className="my-1">{renderInline(para.join(" "))}</p>
    );
  }
  return <div className="space-y-0.5">{blocks}</div>;
}

// Inline parse: **bold**, __bold__, *italic*, _italic_, `code`.
// Emits React nodes, not HTML — safe by construction.
function renderInline(s) {
  const nodes = [];
  const re = /(\*\*[^*\n]+\*\*|__[^_\n]+__|`[^`\n]+`|\*[^*\n]+\*|_[^_\n]+_)/g;
  let last = 0, match, k = 0;
  while ((match = re.exec(s)) !== null) {
    if (match.index > last) nodes.push(s.slice(last, match.index));
    const tok = match[0];
    if (tok.startsWith("**") || tok.startsWith("__")) {
      nodes.push(<strong key={k++} className="font-semibold text-white">{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("`")) {
      nodes.push(<code key={k++} className="rounded bg-black/40 px-1 text-[0.85em]">{tok.slice(1, -1)}</code>);
    } else {
      nodes.push(<em key={k++}>{tok.slice(1, -1)}</em>);
    }
    last = match.index + tok.length;
  }
  if (last < s.length) nodes.push(s.slice(last));
  return nodes;
}

